import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MdlDialogService, MdlDialogReference } from '@angular-mdl/core';
import { ELearningUploadStorage } from '../../storages/e-learning-upload-storage';
import { Settings } from '../../../../core';
import { UPLOAD_DLG_DATA_TOKEN, UploadDialogData } from '../base/upload-dialog-data';
import { SiteSpecificUploadDialogBase } from './site-specific-upload-dialog-base';
import { Protocol } from '../../../shared/protocol';
import { Category, CategoryNaturalKey, Report } from '../../../../documents';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UploadedDocumentsStorage } from '../../storages/uploaded-documents-storage';

@Component({
    selector: 'app-sponsor-communications-upload',
    template: require('./sponsor-communications-upload.component.html'),
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SponsorCommunicationsUploadComponent extends SiteSpecificUploadDialogBase {
    public readonly subcategoryControl: FormControl;
    public readonly protocolControl: FormControl;
    public readonly letterNumberControl: FormControl;
    public readonly titleControl: FormControl;
    public readonly subcategories$: Observable<Category[]>;

    public allowedFileExtensions: string[];

    public get isDocumentLetterNumberVisible() {
        return this.editorForm.get(this.letterNumberControlName) !== null;
    }

    public get categoryChangeConfirmationText(): string {
        const controlsWithValue = [];

        if (this.selectedFiles && this.selectedFiles.size > 0) {
            controlsWithValue.push('Files');
        }
        if (this.letterNumberControl && this.letterNumberControl.value) {
            controlsWithValue.push('Letter Number');
        }
        
        return controlsWithValue.length > 0 ? `${controlsWithValue.join(' and ')} will be cleared.` : null;
    }

    private readonly eLearningCategoryId: CategoryNaturalKey = 'sponsor-communications-e-learning';
    private readonly mainCategoryId: CategoryNaturalKey = 'sponsor-communications-main';

    private readonly letterNumberControlName: string = 'letterNumber';

    constructor(
        protected readonly dialog: MdlDialogReference,
        protected readonly dialogService: MdlDialogService,
        @Inject(UPLOAD_DLG_DATA_TOKEN) protected readonly dialogData: UploadDialogData<Report>,
        uploadedDocumentsStorage: UploadedDocumentsStorage,
        eLearningUploadStorage: ELearningUploadStorage,
        protected readonly settings: Settings
    ) {
        super(dialog, dialogService, uploadedDocumentsStorage, eLearningUploadStorage);

        this.subcategoryControl = new FormControl();
        this.protocolControl = new FormControl();
        this.letterNumberControl = new FormControl();
        this.titleControl = new FormControl();
        this.subcategories$ = dialogData.subCategories$;

        this.editorForm.addControl('subcategoriesControl', this.subcategoryControl);
        this.editorForm.addControl('protocol', this.protocolControl);
        this.editorForm.addControl('title', this.titleControl);

        this.subcategoryControl.valueChanges
            .pipe(takeUntil(this.onDialogDestroying))
            .subscribe((subcategoryId: CategoryNaturalKey) => {
                this.allowedFileExtensions = settings.uploadedFileExtensionsByCategoryId(subcategoryId);

                this.letterNumberControl.reset();
                super.resetFilesControl();

                switch (subcategoryId) {
                    case this.eLearningCategoryId:
                        this.editorForm.removeControl(this.letterNumberControlName);
                        break;
                    case this.mainCategoryId:
                        this.editorForm.addControl(this.letterNumberControlName, this.letterNumberControl);
                        break;
                }
            });
    }

    public async addFiles() {
        const protocol = (this.protocolControl.value as Protocol[])[0];
        const message = `The document will be posted to ${protocol.toString()}. Proceed?`;

        const documentMetadata = {
            protocolId: protocol.id,
            letterNumber: this.letterNumberControl.value,
            title: this.titleControl.value,
        };

        super.addFiles(message, this.subcategoryControl.value, documentMetadata);
    }
}